/** Session keys for pre-game setup (mode → difficulty → car). */

export const GAME_MODE_STORAGE_KEY = "velocityRushGameMode";
export const DIFFICULTY_STORAGE_KEY = "velocityRushDifficulty";
export const SELECTED_CAR_STORAGE_KEY = "velocityRushSelectedCar";
export const MOBILE_CONTROL_STORAGE_KEY = "velocityRushMobileControl";

export const MOBILE_CONTROL_SWIPE = "swipe";
export const MOBILE_CONTROL_ARROWS = "arrows";
export const MOBILE_CONTROL_MOTION = "motion";

export const MOBILE_CONTROL_OPTIONS = [
  {
    id: MOBILE_CONTROL_SWIPE,
    label: "Swipe",
    hint: "Drag the road to steer",
  },
  {
    id: MOBILE_CONTROL_ARROWS,
    label: "Buttons",
    hint: "On-screen arrow buttons",
  },
  {
    id: MOBILE_CONTROL_MOTION,
    label: "Motion",
    hint: "Tilt phone left / right",
  },
];

export function readSessionValue(key) {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

export function writeSessionValue(key, value) {
  try {
    sessionStorage.setItem(key, String(value ?? ""));
  } catch {
    /* ignore */
  }
}

export function readMobileControlMode() {
  const raw = readSessionValue(MOBILE_CONTROL_STORAGE_KEY);
  if (
    raw === MOBILE_CONTROL_SWIPE ||
    raw === MOBILE_CONTROL_ARROWS ||
    raw === MOBILE_CONTROL_MOTION
  ) {
    return raw;
  }
  return MOBILE_CONTROL_SWIPE;
}

export const DIFFICULTY_OPTIONS = [
  { id: "easy", label: "EASY" },
  { id: "medium", label: "MEDIUM" },
  { id: "hard", label: "HARD" },
];

export const DIFFICULTY_THEME_KEYS = {
  easy: "endless_easy",
  medium: "endless_medium",
  hard: "endless_hard",
};

export function isThemeFlagOn(val, defaultOn = false) {
  if (val == null || val === "") return defaultOn;
  return String(val).toLowerCase() === "true";
}

/** Endless difficulties the admin left on. Missing keys count as on. */
export function getEnabledDifficulties(themeData) {
  const enabled = DIFFICULTY_OPTIONS.filter((opt) =>
    isThemeFlagOn(themeData?.[DIFFICULTY_THEME_KEYS[opt.id]], true)
  );
  return enabled.length ? enabled : DIFFICULTY_OPTIONS.slice();
}

export const ENDLESS_MODE_ID = "endless_runner";
export const QUIZ_MODE_ID = "quiz_runner";
export const LOGIN_BACK_PATH = "/login?&save=true";
export const PLAYER_LANGUAGE_STORAGE_KEY = "snakesLaddersLanguage";

export function readPlayerLanguage() {
  const raw = String(readSessionValue(PLAYER_LANGUAGE_STORAGE_KEY) || "").toLowerCase();
  if (raw === "english" || raw === "hindi" || raw === "marathi") return raw;
  return "english";
}

export function writePlayerLanguage(lang) {
  const next = String(lang || "english").toLowerCase();
  writeSessionValue(
    PLAYER_LANGUAGE_STORAGE_KEY,
    next === "hindi" || next === "marathi" ? next : "english"
  );
}

export function getEnabledModes(themeData) {
  const modes = [];
  if (isThemeFlagOn(themeData?.endless_runner, true)) modes.push(ENDLESS_MODE_ID);
  if (isThemeFlagOn(themeData?.quiz_runner, true)) modes.push(QUIZ_MODE_ID);
  return modes;
}

function applyModeDefaults(modeId, themeData) {
  writeSessionValue(GAME_MODE_STORAGE_KEY, modeId);
  if (modeId === QUIZ_MODE_ID) {
    writeSessionValue(DIFFICULTY_STORAGE_KEY, "medium");
    return;
  }
  const diffs = getEnabledDifficulties(themeData);
  if (diffs.length < 2) {
    writeSessionValue(DIFFICULTY_STORAGE_KEY, diffs[0]?.id || "medium");
  }
}

/** After a mode is known: snakes & ladders goes straight to the board. */
export function getPathAfterMode(modeId, themeData) {
  const id = modeId || ENDLESS_MODE_ID;
  applyModeDefaults(id, themeData);
  return "/game";
}

/** First player screen after login / Begin Play. */
export function getFirstSetupPath(themeData) {
  if (isThemeFlagOn(themeData?.MultiLanguage, false)) return "/language";
  writePlayerLanguage("english");
  return "/rules";
}

export function getBackUrlFromDifficulty(themeData) {
  return getEnabledModes(themeData).length >= 2 ? "/rules" : LOGIN_BACK_PATH;
}

export function getBackUrlFromSelection(themeData) {
  if (getEnabledModes(themeData).length >= 2) return "/rules";
  return LOGIN_BACK_PATH;
}

/** Base race speed multipliers by difficulty. */
export const DIFFICULTY_SPEED = {
  easy: 4.5,
  medium: 6,
  hard: 8,
};
